/**
 * Duplicate Area Modal Component
 * Modal for duplicating a location with optional cleaning standards copy
 * Aligns with POST /locations/{locationId}/duplicate API
 */

import React, { useState, useEffect } from 'react';
import { X, Copy, CheckCircle2, Loader2 } from 'lucide-react';
import {
  INFO_CARD_WRAPPER,
  INFO_CARD_WRAPPER_P5,
  INFO_CARD_LABEL,
  INFO_CARD_VALUE,
  INFO_CARD_TITLE,
  INFO_CARD_TEXT,
} from '../standards/constants/infoCardStyles';

/**
 * @typedef {Object} Location
 * @property {string} id
 * @property {string} name
 * @property {string} facilityId
 * @property {string} [cleaningStandardsStatus]
 */

/**
 * @typedef {Object} DuplicateLocationModalProps
 * @property {boolean} isOpen
 * @property {() => void} onClose
 * @property {(payload: { newName: string, includeStandards: boolean }) => void} onConfirm
 * @property {Location} location
 * @property {boolean} [hasStandards] - Whether the location has cleaning standards set
 */
export const DuplicateLocationModal = ({
  isOpen,
  onClose,
  onConfirm,
  location,
  hasStandards = false,
}) => {
  const [newName, setNewName] = useState('');
  const [includeStandards, setIncludeStandards] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (location?.name) {
      setNewName(`${location.name} - Copy`);
    }
  }, [location?.name]);

  const handleConfirm = async () => {
    const trimmed = newName.trim();
    if (!trimmed || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onConfirm({ newName: trimmed, includeStandards });
      onClose();
    } catch (e) {
      // Parent shows error toast; keep modal open so user can retry
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (location?.name) setNewName(`${location.name} - Copy`);
    setIncludeStandards(true);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-[rgba(184,182,182,0.5)] z-40" onClick={handleCancel} />

      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-[10px] px-8 py-8 w-[90vw] max-w-[580px] z-50 shadow-2xl">
        <div className="flex flex-col gap-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#61b8e7]/10 flex items-center justify-center">
                <Copy className="w-5 h-5 text-[#61b8e7]" />
              </div>
              <h2 className="font-['Roboto:Bold',sans-serif] font-bold text-[24px] text-black">
                Duplicate Area
              </h2>
            </div>

            <button
              type="button"
              onClick={handleCancel}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-[#F2F4F8] hover:bg-gray-300 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-[#1C1B1F]" />
            </button>
          </div>

          {/* Original location name - shared info card style */}
          <div className={INFO_CARD_WRAPPER}>
            <p className={INFO_CARD_LABEL}>Duplicating from:</p>
            <p className={INFO_CARD_VALUE}>{location?.name}</p>
          </div>

          {/* New location name input */}
          <div>
            <label className="block font-['Roboto:Medium',sans-serif] text-[14px] text-gray-700 mb-2">
              New location name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. OR 1 - Copy"
              className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 font-['Roboto:Regular',sans-serif] text-[15px] focus:border-[#61b8e7] focus:outline-none transition-colors"
              autoFocus
            />
          </div>

          {/* Include standards checkbox - shared info card style */}
          <div className={INFO_CARD_WRAPPER_P5}>
            <p className={`${INFO_CARD_TITLE} mb-3`}>Options</p>
            <label className="flex items-center gap-3 cursor-pointer group">
              <div className="relative shrink-0">
                <input
                  type="checkbox"
                  checked={includeStandards}
                  onChange={(e) => setIncludeStandards(e.target.checked)}
                  className="size-[18px] appearance-none bg-white border-2 border-[#9e9e9e] rounded-[3px] cursor-pointer checked:bg-[#61b8e7] checked:border-[#61b8e7] transition-all group-hover:border-[#61b8e7]"
                  aria-label="Include cleaning standards"
                />
                {includeStandards && (
                  <svg
                    className="absolute left-[3px] top-[3px] w-[12px] h-[12px] pointer-events-none"
                    fill="none"
                    viewBox="0 0 12 10"
                  >
                    <path
                      d="M1.5 5L4.5 8L10.5 1.5"
                      stroke="white"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
              <span className={INFO_CARD_TEXT}>
                Include cleaning standards
                {hasStandards ? (
                  <span className="text-[#10b981] font-['Roboto:SemiBold',sans-serif] ml-1">
                    (configured)
                  </span>
                ) : (
                  <span className="text-gray-500 ml-1">(none set)</span>
                )}
              </span>
            </label>
            <p className={`${INFO_CARD_TEXT} mt-2 ml-7`}>
              If unchecked, the new area will be created without standards.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={handleCancel}
              className="border-2 border-gray-300 bg-white text-gray-700 rounded-lg px-6 py-2.5 font-['Roboto:SemiBold',sans-serif] font-semibold text-[15px] hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!newName.trim() || isSubmitting}
              className="flex items-center gap-2 bg-[#61b8e7] text-white rounded-lg px-6 py-2.5 font-['Roboto:SemiBold',sans-serif] font-semibold text-[15px] hover:bg-[#4da3d1] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" aria-hidden />
                  Duplicating...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5" />
                  Duplicate Area
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
